import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

const categoryIconsMobile: Record<string, string> = {
  shoes: 'ri-footprint-line',
  hoodies: 'ri-t-shirt-line',
  tshirts: 'ri-shirt-line',
  jackets: 'ri-honour-line',
  pants: 'ri-pantone-line',
  headwear: 'ri-user-smile-line',
  sets: 'ri-stack-line',
  underwear: 'ri-checkbox-blank-line',
  jersey: 'ri-star-line',
  accessories: 'ri-glasses-line',
  bags: 'ri-briefcase-line',
  belts: 'ri-link',
  perfumes: 'ri-drop-line',
  otherstuff: 'ri-more-line',
};

const categoryColors: Record<string, string> = {
  shoes: 'bg-primary-100 text-primary-700',
  hoodies: 'bg-secondary-100 text-secondary-700',
  tshirts: 'bg-accent-100 text-accent-700',
  jackets: 'bg-foreground-100 text-foreground-700',
  pants: 'bg-primary-100 text-primary-800',
  headwear: 'bg-secondary-100 text-secondary-800',
  sets: 'bg-accent-100 text-accent-800',
  underwear: 'bg-foreground-100 text-foreground-800',
  jersey: 'bg-primary-100 text-primary-600',
  accessories: 'bg-secondary-100 text-secondary-600',
  bags: 'bg-accent-100 text-accent-600',
  belts: 'bg-foreground-100 text-foreground-600',
  perfumes: 'bg-primary-100 text-primary-500',
  otherstuff: 'bg-secondary-100 text-secondary-500',
};

export default function CategoryDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-md mx-auto">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl bg-white/15 backdrop-blur-sm border border-white/25 text-white text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-white/25"
      >
        <span className="flex items-center gap-2">
          <i className="ri-grid-line text-base"></i>
          All Categories
        </span>
        <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40 md:hidden"
            onClick={() => setOpen(false)}
          />
          <div className="absolute md:relative top-full mt-2 left-0 w-full bg-background-50 rounded-xl border border-background-200/70 overflow-hidden z-50 max-h-[70vh] md:max-h-[420px] overflow-y-auto">
            <div className="sticky top-0 bg-background-50 px-5 py-3 border-b border-background-200/60 flex items-center justify-between md:hidden">
              <span className="font-heading font-semibold text-sm text-foreground-900">All Categories</span>
              <button
                onClick={() => setOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-0 p-2 md:p-0">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-4 px-4 py-4 md:py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap rounded-lg mx-1"
                >
                  <span className={`w-10 h-10 flex items-center justify-center rounded-xl ${categoryColors[cat.id] || 'bg-background-200 text-foreground-600'}`}>
                    <i className={`${categoryIconsMobile[cat.id] || cat.icon} text-base`}></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-foreground-900 block">{cat.name}</span>
                    <span className="text-xs text-foreground-500 block truncate">{cat.description}</span>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}